import { useCallback, useContext, useMemo, useState } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Slider from 'LuxKit/Slider/Slider'
import FormatCurrency from 'components/Common/FormatCurrency'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Colour from 'components/utils/Colour'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import useDebouncedCallback from 'hooks/useDebouncedCallback'
import LoyaltyCardCompact from 'loyalty/components/LoyaltyCardCompact'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import useLoyaltyPointsEarnCalculator from 'loyalty/hooks/useLoyaltyPointsEarnCalculator'
import { generateLoyaltyPointsCalculatorMerchandiseOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { POINTS_TO_DOLLARS_RATE } from 'constants/merchandise'
import { floatify } from 'lib/maths/mathUtils'
import { fillArray } from 'lib/array/arrayUtils'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import PriceRowLoyaltyPointsLoadingSkeleton from 'LuxKit/PricePoints/Loyalty/PriceRowLoyaltyPointsLoadingSkeleton'
import PriceRowLoyaltyPoints from 'LuxKit/PricePoints/Loyalty/PriceRowLoyaltyPoints'
import PriceRowLoyaltyPointsPromoPill from 'LuxKit/PricePoints/Loyalty/PriceRowLoyaltyPointsPromoPill'
import determineLoyaltyBonusPointsCampaignType from 'loyalty/lib/determineLoyaltyBonusPointsCampaignType'
import ModalContext from 'contexts/ModalContext'
import MerchandisePointsPayCalculatorInfoModal from './MerchandisePointsPayCalculatorInfoModal'

const CalculatorHeader = styled(Sheet)`
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

interface Props {
  offer: App.MerchandiseOffer
  variant: App.MerchandiseOfferVariant
  quantity: number
}

function MerchandiseOfferPagePointsPayCalculator(props: Props) {
  const { offer, variant, quantity } = props
  const intl = useIntl()
  const [pointsToSpend, setPointsToSpend] = useState(0)

  const priceToPay = useMemo(() => {
    const pointsAsDollars = floatify(pointsToSpend * POINTS_TO_DOLLARS_RATE)
    return (variant.price ?? 0) - pointsAsDollars
  }, [pointsToSpend, variant])

  const [tier] = useCurrentLoyaltyTier()
  const showModal = useContext(ModalContext)

  const debouncedSetPointsToSpend = useDebouncedCallback(setPointsToSpend, 100, [setPointsToSpend])
  const onSliderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSetPointsToSpend(e.currentTarget.valueAsNumber)
  }, [debouncedSetPointsToSpend])

  const pointsCalcs = useMemo(() => {
    return fillArray(quantity, () => generateLoyaltyPointsCalculatorMerchandiseOptions(variant, {
      price: variant.price,
    }))
  }, [variant, quantity])

  const { pointsCalculations, totalPointsCalculation } = useLoyaltyPointsEarnCalculator(pointsCalcs)
  const firstPointsCalculation = pointsCalculations[0]

  const isFetching = totalPointsCalculation.fetching

  const pointsToDisplay = useMemo(() => {
    if (!totalPointsCalculation.data) return 0
    return Math.round(totalPointsCalculation.data.roundedPointsEarnRatio * priceToPay)
  }, [totalPointsCalculation, priceToPay])

  const bonusPointsCampaignTypes = useMemo(() => {
    if (!totalPointsCalculation.data) return undefined
    return determineLoyaltyBonusPointsCampaignType(totalPointsCalculation.data)
  }, [totalPointsCalculation])

  const openCalculatorInfoModal = useCallback(() => {
    showModal(<MerchandisePointsPayCalculatorInfoModal
      offerName={offer.name}
      quantity={quantity}
      pointsCalculation={totalPointsCalculation.data}
    />)
  }, [showModal, offer.name, quantity, totalPointsCalculation.data])

  return <Sheet
    border="light-foreground"
  >
    <CalculatorHeader padding="16 20">
      <Group direction="horizontal" gap={12} verticalAlign="center">
        <LoyaltyCardCompact tier={tier} />
        <Heading variant="heading6">
          {intl.formatMessage(
            {
              id: '6IdLwKolCs4rJ4hWM4C9',
              defaultMessage: 'Pay with {programName} Points Calculator',
              description: 'Heading for points payment calculator on merchandise offer page',
            },
            { programName: getLoyaltyProgramDisplayName() },
          )}
        </Heading>
      </Group>
    </CalculatorHeader>
    <Sheet padding={20}>
      <VerticalSpacer gap={16}>
        <BodyText variant="large" align="end">
          <FormattedMessage
            id="Nqlv8wbOzoH56lleDxuv"
            defaultMessage="{points} <bold>+ {price}</bold>"
            description="Points and price combined display showing loyalty points plus currency amount"
            values={{
              points: <Colour value="light-foreground-alt"><FormatLoyaltyPoints abbreviated points={pointsToSpend * quantity} /></Colour>,
              price: <FormatCurrency value={priceToPay * quantity} />,
              bold: (chunks: React.ReactNode) => <b>{chunks}</b>,
            }}
          />
        </BodyText>
        <VerticalSpacer gap={8}>
          <Slider
            defaultValue={pointsToSpend}
            min={0}
            max={(variant.pointsPrice ?? 0)}
            onChange={onSliderChange}
          />
          <Group direction="horizontal" gap={12} horizontalAlign="space-between">
            <BodyText variant="small" colour="light-foreground-alt">
              <FormatLoyaltyPoints points={0} abbreviated />
            </BodyText>
            <BodyText variant="small">
              <FormatLoyaltyPoints points={(variant.pointsPrice ?? 0) * quantity} abbreviated />
            </BodyText>
          </Group>

        </VerticalSpacer>
        {!!priceToPay && <VerticalSpacer gap={8}>
          {isFetching && <>
            <TextLoadingBox width="22ch" typography="body-small" />
            <PriceRowLoyaltyPointsLoadingSkeleton tier={tier} size="S" />
          </>}
          {!isFetching && <>
            {!!pointsToDisplay && !!bonusPointsCampaignTypes && bonusPointsCampaignTypes.length === 0 && <PriceRowLoyaltyPoints
              tier={tier}
              earnablePoints={pointsToDisplay}
              size="S"
              calculationType="exact"
              onInfoIconClick={openCalculatorInfoModal}
            />}
            {!!pointsToDisplay && !!bonusPointsCampaignTypes && bonusPointsCampaignTypes.length > 0 && <PriceRowLoyaltyPointsPromoPill
              campaigns={bonusPointsCampaignTypes}
              tier={tier}
              earnablePoints={pointsToDisplay}
              size="S"
              calculationType="exact"
              onInfoIconClick={openCalculatorInfoModal}
            />}
            {!!firstPointsCalculation?.data?.roundedPointsEarnRatio && (
              <BodyText variant="small" colour="light-foreground-alt">
                <FormattedMessage
                  id="jZmqG4e2SOBhdTBc7l86"
                  defaultMessage="Calculated as {points} per {currency} spend"
                  description="Text explaining points earn rate calculation in the merchandise shop section"
                  values={{
                    points: <FormatLoyaltyPoints points={firstPointsCalculation.data.roundedPointsEarnRatio} abbreviated />,
                    currency: <FormatCurrency value={1} />,
                  }}
                />
              </BodyText>
            )}
          </>}
        </VerticalSpacer>}
      </VerticalSpacer>
    </Sheet>
  </Sheet>
}

export default MerchandiseOfferPagePointsPayCalculator
